// UCC tab — lien search card. Primary path orders a Baselayer lien search
// (RunCheckModal pre-seeded with the loan's deal, debtor address, and —
// when one exists — the newest completed KYB's verified business). The
// secondary "Record an external search" path keeps the manual entry flow.

import { useState } from "react";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, SkeletonList } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { uccPrefillApi } from "@/api/portfolioAdmin/uccPrefillApi";
import {
  useBackgroundCheck,
  useBackgroundChecks,
} from "@/hooks/backgroundChecks/useBackgroundChecks";
import { RunCheckModal } from "@/components/backgroundChecks/RunCheckModal";
import { LienSearchHistoryTable } from "./LienSearchHistoryTable";
import { OrderLienSearchModal } from "./OrderLienSearchModal";
import type { LoanSummaryDto, UccLienSearchDto } from "@/api/portfolioAdmin/types";
import type { BackgroundCheckDto } from "@/api/backgroundChecks/backgroundChecksApi";

interface LienSearchCardProps {
  loan: LoanSummaryDto;
  searches: UccLienSearchDto[];
  isLoading: boolean;
  isError: boolean;
}

/** Defensive client-side parse of the KYB result payload for the Baselayer
 *  business id (the established Results-Explorer pattern — no typed DTOs). */
export function parseBaselayerBusinessId(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { business?: { id?: unknown } };
    const id = parsed?.business?.id;
    if (typeof id === "string" && id) return id;
    if (typeof id === "number") return String(id);
    return null;
  } catch {
    return null;
  }
}

function newestCompletedKyb(
  checks: BackgroundCheckDto[] | undefined,
): BackgroundCheckDto | null {
  if (!checks) return null;
  return (
    checks
      .filter(
        (c) =>
          c.checkType === "Kyb" &&
          (c.status === "Completed" || c.status === "Filed"),
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
  );
}

/** Mounted only while the order modal is open, so the prefill + check
 *  lookups never fire on plain tab loads. */
function OrderBaselayerLienSearch({
  loan,
  onClose,
}: {
  loan: LoanSummaryDto;
  onClose: () => void;
}) {
  const prefillQuery = useQuery({
    queryKey: ["portfolio-admin", "ucc-debtor-prefill", loan.id],
    queryFn: () => uccPrefillApi.getDebtorPrefill(loan.id),
  });
  const checksQuery = useBackgroundChecks({ dealId: loan.dealId });
  const completedKyb = newestCompletedKyb(checksQuery.data);
  const detailQuery = useBackgroundCheck(completedKyb?.id);

  const prefill = prefillQuery.data;
  // undefined while we're still looking; null = looked, none found.
  const knownBusinessName = checksQuery.isSuccess
    ? completedKyb?.subjectName ?? null
    : undefined;
  const knownBusinessId = parseBaselayerBusinessId(
    detailQuery.data?.result?.rawResponseJson,
  );

  return (
    <RunCheckModal
      open
      onClose={onClose}
      dealId={loan.dealId}
      loanId={loan.id}
      initialType="LienSearch"
      prefillAddress={
        prefill
          ? {
              street: prefill.debtorMailingLine1,
              city: prefill.debtorCity,
              state: prefill.debtorState,
              zip: prefill.debtorZip,
            }
          : null
      }
      knownBusinessId={knownBusinessId}
      knownBusinessName={knownBusinessName}
    />
  );
}

export function LienSearchCard({
  loan,
  searches,
  isLoading,
  isError,
}: LienSearchCardProps) {
  const auth = useAuth();
  const canOrderBaselayer =
    auth.hasPermission("BackgroundCheck.RunLienSearch") ||
    auth.hasRoleCode("ADMIN");
  const canRecordManual =
    auth.hasPermission("UCC.OrderSearch") || auth.hasRoleCode("ADMIN");

  const [orderOpen, setOrderOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);

  return (
    <Card data-testid="ucc-lien-search-card">
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Lien searches</h3>
        <div className="flex items-center gap-space-2">
          {canRecordManual && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setRecordOpen(true)}
              data-testid="ucc-record-search-button"
            >
              Record an external search
            </Button>
          )}
          {canOrderBaselayer && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setOrderOpen(true)}
              data-testid="ucc-order-lien-search-button"
            >
              <Plus size={14} aria-hidden /> Order lien search
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <SkeletonList rows={3} data-testid="ucc-searches-loading" />
      ) : isError ? (
        <p className="text-small text-status-error m-0" role="alert">
          Could not load lien searches. Refresh to retry.
        </p>
      ) : searches.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No lien searches recorded for this loan.
        </p>
      ) : (
        <LienSearchHistoryTable loanId={loan.id} searches={searches} />
      )}

      {orderOpen && (
        <OrderBaselayerLienSearch loan={loan} onClose={() => setOrderOpen(false)} />
      )}
      {recordOpen && (
        <OrderLienSearchModal
          loanId={loan.id}
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
        />
      )}
    </Card>
  );
}
